import { useState } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

interface Props {
  onClose: () => void;
}

type TipoRelatorio = 'funcionario' | 'orcamental' | 'geral';

export default function ExportPdfFormacao({ onClose }: Props) {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();
  const [tipo, setTipo] = useState<TipoRelatorio>('geral');
  const [anoFiltro, setAnoFiltro] = useState('2025');
  const [generating, setGenerating] = useState(false);

  const anos = ['2024', '2025', '2026'];

  function gerarPdf() {
    setGenerating(true);
    setTimeout(() => {
      if (tipo === 'funcionario') gerarRelatorioPorFuncionario();
      else if (tipo === 'orcamental') gerarRelatorioOrcamental();
      else gerarRelatorioGeral();
      setGenerating(false);
      onClose();
    }, 600);
  }

  // ── Utilitários ──────────────────────────────────────────────────────────────
  function formatEuro(v: number) {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
  }

  function nowStr() {
    return new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  // ── CSS partilhado ───────────────────────────────────────────────────────────
  const baseCSS = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; }
    .page { max-width: 900px; margin: 0 auto; padding: 32px 40px; }
    .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
    .header-title { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; color: #111; }
    .header-sub { font-size: 11px; color: #666; margin-top: 4px; }
    .header-meta { text-align: right; font-size: 10px; color: #999; }
    .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #333; margin: 20px 0 8px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th { background: #f5f5f5; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #555; padding: 6px 8px; text-align: left; border-bottom: 1px solid #ddd; }
    td { font-size: 10px; padding: 5px 8px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
    tr:last-child td { border-bottom: none; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
    .kpi-box { background: #f8f8f8; border: 1px solid #e8e8e8; border-radius: 6px; padding: 12px; }
    .kpi-val { font-size: 20px; font-weight: 800; color: #111; }
    .kpi-lbl { font-size: 9px; color: #888; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-top: 2px; }
    .badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 3px; }
    .badge-green { background: #dcfce7; color: #166534; }
    .badge-zinc { background: #f4f4f5; color: #52525b; }
    .badge-amber { background: #fef3c7; color: #92400e; }
    .badge-rose { background: #fee2e2; color: #991b1b; }
    .bar { height: 6px; background: #e5e7eb; border-radius: 3px; margin: 3px 0; }
    .bar-fill { height: 100%; border-radius: 3px; }
    .bar-green { background: #22c55e; }
    .bar-amber { background: #f59e0b; }
    .bar-rose { background: #f43f5e; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e5e5; display: flex; justify-content: space-between; font-size: 9px; color: #aaa; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: 700; }
    .text-green { color: #16a34a; }
    .text-rose { color: #e11d48; }
    .text-amber { color: #d97706; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;

  // ── Relatório Geral ──────────────────────────────────────────────────────────
  function gerarRelatorioGeral() {
    const formacoesAno = formacoes.filter((f) => {
      const ano = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      return ano === anoFiltro;
    });
    const partsAno = participantes.filter((p) => {
      const f = formacoes.find((fc) => fc.id === p.formacao_id);
      return ((f?.data_inicio ?? f?.data_fim ?? '').slice(0, 4)) === anoFiltro;
    });

    const concluidas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const totalHoras = partsAno.reduce((s, p) => {
      const f = formacoes.find((fc) => fc.id === p.formacao_id);
      return s + (Number(f?.duracao_horas) || 0);
    }, 0);
    const totalCusto = formacoesAno.reduce((s, f) => s + Number(f.custo || 0), 0);
    const totalOrc = formacoesAno.reduce((s, f) => s + Number(f.orcamento_previsto || 0), 0);
    const certs = partsAno.filter((p) => p.certificado_emitido).length;
    const taxaCert = partsAno.length > 0 ? Math.round((certs / partsAno.length) * 100) : 0;
    const funcComForm = new Set(partsAno.map((p) => p.funcionario_id)).size;
    const taxaCobertura = funcionarios.length > 0 ? Math.round((funcComForm / funcionarios.length) * 100) : 0;

    const rows = formacoesAno.map((f) => {
      const parts = participantes.filter((p) => p.formacao_id === f.id);
      const certsF = parts.filter((p) => p.certificado_emitido).length;
      return { ...f, nParts: parts.length, certsF };
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Relatório de Formação ${anoFiltro}</title>
    <style>${baseCSS}</style></head><body><div class="page">
    <div class="header">
      <div>
        <div class="header-title">Relatório de Formação ${anoFiltro}</div>
        <div class="header-sub">Gestão da Formação Municipal · Resumo Anual</div>
      </div>
      <div class="header-meta">
        <div>Emitido em ${nowStr()}</div>
        <div>${funcionarios.length} funcionários · ${formacoesAno.length} ações</div>
      </div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-box"><div class="kpi-val">${funcComForm}</div><div class="kpi-lbl">Funcionários Formados</div></div>
      <div class="kpi-box"><div class="kpi-val">${taxaCobertura}%</div><div class="kpi-lbl">Cobertura</div></div>
      <div class="kpi-box"><div class="kpi-val">${totalHoras}h</div><div class="kpi-lbl">Horas de Formação</div></div>
      <div class="kpi-box"><div class="kpi-val">${taxaCert}%</div><div class="kpi-lbl">Taxa de Certificação</div></div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-box"><div class="kpi-val">${partsAno.length}</div><div class="kpi-lbl">Participações</div></div>
      <div class="kpi-box"><div class="kpi-val">${certs}</div><div class="kpi-lbl">Certificados</div></div>
      <div class="kpi-box"><div class="kpi-val">${concluidas.length}/${formacoesAno.length}</div><div class="kpi-lbl">Ações Concluídas</div></div>
      <div class="kpi-box"><div class="kpi-val">${formatEuro(totalCusto)}</div><div class="kpi-lbl">Investimento Total</div></div>
    </div>

    <div class="section-title">Ações de Formação — ${anoFiltro}</div>
    <table>
      <thead><tr>
        <th>Ação de Formação</th>
        <th>Entidade</th>
        <th>Área</th>
        <th class="text-center">Horas</th>
        <th class="text-center">Part.</th>
        <th class="text-center">Cert.</th>
        <th class="text-right">Custo</th>
        <th class="text-center">Estado</th>
      </tr></thead>
      <tbody>
        ${rows.map((r) => `<tr>
          <td class="font-bold">${r.nome}</td>
          <td>${r.entidade_formadora ?? '—'}</td>
          <td>${r.categoria ?? '—'}</td>
          <td class="text-center">${r.duracao_horas}h</td>
          <td class="text-center">${r.nParts}</td>
          <td class="text-center ${r.certsF > 0 ? 'text-green font-bold' : ''}">${r.certsF}</td>
          <td class="text-right">${r.custo > 0 ? formatEuro(r.custo) : '—'}</td>
          <td class="text-center"><span class="badge ${estadoNormalizado(r.estado) === 'concluida' ? 'badge-green' : 'badge-zinc'}">${estadoNormalizado(r.estado) === 'concluida' ? 'Concluída' : 'Planeada'}</span></td>
        </tr>`).join('')}
        <tr style="background:#f8f8f8; font-weight:700;">
          <td colspan="3">TOTAL</td>
          <td class="text-center">—</td>
          <td class="text-center">${partsAno.length}</td>
          <td class="text-center">${certs}</td>
          <td class="text-right">${formatEuro(totalCusto)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div class="footer">
      <div>Gestão da Formação Municipal — Relatório Confidencial</div>
      <div>Gerado automaticamente em ${nowStr()}</div>
    </div>
    </div></body></html>`;

    abrirJanela(html, `relatorio-formacao-${anoFiltro}.pdf`);
  }

  // ── Relatório por Funcionário ─────────────────────────────────────────────────
  function gerarRelatorioPorFuncionario() {
    const formacoesAno = formacoes.filter((f) => {
      const ano = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      return ano === anoFiltro;
    });
    const idsFormAno = new Set(formacoesAno.map((f) => f.id));
    const partsAno = participantes.filter((p) => idsFormAno.has(p.formacao_id));

    const rows = funcionarios.map((func) => {
      const fp = partsAno.filter((p) => p.funcionario_id === func.id);
      const horas = fp.reduce((s, p) => {
        const f = formacoes.find((fc) => fc.id === p.formacao_id);
        return s + (Number(f?.duracao_horas) || 0);
      }, 0);
      const certs = fp.filter((p) => p.certificado_emitido).length;
      const ultima = fp.reduce((max, p) => {
        const f = formacoes.find((fc) => fc.id === p.formacao_id);
        const d = f?.data_fim ?? f?.data_inicio ?? '';
        return d > max ? d : max;
      }, '');
      return { ...func, nForm: fp.length, horas, certs, ultima, taxaCert: fp.length > 0 ? Math.round((certs / fp.length) * 100) : null };
    }).sort((a, b) => b.horas - a.horas);

    const semForm = rows.filter((r) => r.nForm === 0).length;
    const comForm = rows.filter((r) => r.nForm > 0).length;
    const totalHoras = rows.reduce((s, r) => s + r.horas, 0);
    const totalCerts = rows.reduce((s, r) => s + r.certs, 0);

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Relatório por Funcionário ${anoFiltro}</title>
    <style>${baseCSS}
    .status-bar { height: 8px; border-radius: 4px; background: #e5e7eb; margin: 6px 0; }
    .status-bar-fill { height: 100%; border-radius: 4px; background: #111; }
    </style></head><body><div class="page">
    <div class="header">
      <div>
        <div class="header-title">Relatório de Formação por Funcionário</div>
        <div class="header-sub">Gestão da Formação Municipal · Ano ${anoFiltro}</div>
      </div>
      <div class="header-meta">
        <div>Emitido em ${nowStr()}</div>
        <div>${funcionarios.length} funcionários analisados</div>
      </div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-box"><div class="kpi-val">${comForm}</div><div class="kpi-lbl">Com Formação</div></div>
      <div class="kpi-box"><div class="kpi-val">${semForm}</div><div class="kpi-lbl">Sem Formação</div></div>
      <div class="kpi-box"><div class="kpi-val">${totalHoras}h</div><div class="kpi-lbl">Total de Horas</div></div>
      <div class="kpi-box"><div class="kpi-val">${totalCerts}</div><div class="kpi-lbl">Certificados</div></div>
    </div>

    <div class="section-title">Detalhe por Funcionário</div>
    <table>
      <thead><tr>
        <th>#</th>
        <th>Nome</th>
        <th>Carreira</th>
        <th>Unidade Orgânica</th>
        <th class="text-center">Ações</th>
        <th class="text-center">Horas</th>
        <th class="text-center">Certificados</th>
        <th class="text-center">Taxa Cert.</th>
        <th>Última Formação</th>
      </tr></thead>
      <tbody>
        ${rows.map((r, i) => `<tr>
          <td class="text-center" style="color:#bbb; font-weight:700;">${i + 1}</td>
          <td class="font-bold">${r.nome}</td>
          <td style="color:#666">${r.carreira ?? '—'}</td>
          <td style="color:#666; font-size:9px">${(r as { unidade_organica?: string }).unidade_organica ?? '—'}</td>
          <td class="text-center">${r.nForm === 0 ? '<span style="color:#bbb">0</span>' : `<span class="font-bold">${r.nForm}</span>`}</td>
          <td class="text-center">${r.horas === 0 ? '<span style="color:#bbb">—</span>' : `<span class="font-bold">${r.horas}h</span>`}</td>
          <td class="text-center ${r.certs > 0 ? 'text-green font-bold' : ''}">${r.certs === 0 ? '<span style="color:#bbb">—</span>' : r.certs}</td>
          <td class="text-center">${r.taxaCert !== null ? `<span class="${r.taxaCert === 100 ? 'text-green font-bold' : r.taxaCert >= 70 ? 'text-amber' : 'text-rose'}">${r.taxaCert}%</span>` : '<span style="color:#bbb">—</span>'}</td>
          <td style="color:#666">${r.ultima ? new Date(r.ultima).toLocaleDateString('pt-PT') : '<span class="badge badge-rose">Sem registo</span>'}</td>
        </tr>`).join('')}
      </tbody>
    </table>

    <div class="section-title">Funcionários sem Formação em ${anoFiltro}</div>
    ${semForm > 0 ? `<table>
      <thead><tr><th>#</th><th>Nome</th><th>Carreira</th><th>Unidade Orgânica</th></tr></thead>
      <tbody>
        ${rows.filter((r) => r.nForm === 0).map((r, i) => `<tr>
          <td class="text-center" style="color:#bbb">${i + 1}</td>
          <td class="font-bold">${r.nome}</td>
          <td style="color:#666">${r.carreira ?? '—'}</td>
          <td style="color:#666; font-size:9px">${(r as { unidade_organica?: string }).unidade_organica ?? '—'}</td>
        </tr>`).join('')}
      </tbody>
    </table>` : '<p style="color:#16a34a; font-weight:700; font-size:11px;">Todos os funcionários tiveram formação em ' + anoFiltro + '.</p>'}

    <div class="footer">
      <div>Gestão da Formação Municipal — Relatório Confidencial</div>
      <div>Gerado automaticamente em ${nowStr()}</div>
    </div>
    </div></body></html>`;

    abrirJanela(html, `relatorio-funcionarios-${anoFiltro}.pdf`);
  }

  // ── Relatório Orçamental ──────────────────────────────────────────────────────
  function gerarRelatorioOrcamental() {
    const formacoesAno = formacoes.filter((f) => {
      const ano = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      return ano === anoFiltro;
    });

    const totalPrev = formacoesAno.reduce((s, f) => s + Number(f.orcamento_previsto || 0), 0);
    const totalReal = formacoesAno.reduce((s, f) => s + Number(f.custo || 0), 0);
    const desvio = totalReal - totalPrev;
    const pctExec = totalPrev > 0 ? Math.round((totalReal / totalPrev) * 100) : 0;
    const concluidas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const totalRealConc = concluidas.reduce((s, f) => s + Number(f.custo || 0), 0);
    const totalPrevConc = concluidas.reduce((s, f) => s + Number(f.orcamento_previsto || 0), 0);

    // Agrupado por financiamento
    const porFin: Record<string, { prev: number; real: number; count: number }> = {};
    formacoesAno.forEach((f) => {
      const fin = f.financiamento ?? 'Não especificado';
      if (!porFin[fin]) porFin[fin] = { prev: 0, real: 0, count: 0 };
      porFin[fin].prev += Number(f.orcamento_previsto || 0);
      porFin[fin].real += Number(f.custo || 0);
      porFin[fin].count += 1;
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Relatório Orçamental de Formação ${anoFiltro}</title>
    <style>${baseCSS}</style></head><body><div class="page">
    <div class="header">
      <div>
        <div class="header-title">Análise Orçamental de Formação</div>
        <div class="header-sub">Gestão da Formação Municipal · Ano ${anoFiltro} · Execução Orçamental</div>
      </div>
      <div class="header-meta">
        <div>Emitido em ${nowStr()}</div>
        <div>${formacoesAno.length} ações · ${concluidas.length} concluídas</div>
      </div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-box"><div class="kpi-val">${formatEuro(totalPrev)}</div><div class="kpi-lbl">Orçamento Previsto</div></div>
      <div class="kpi-box"><div class="kpi-val">${formatEuro(totalReal)}</div><div class="kpi-lbl">Custo Real</div></div>
      <div class="kpi-box"><div class="kpi-val ${desvio > 0 ? 'text-rose' : 'text-green'}">${desvio > 0 ? '+' : ''}${formatEuro(desvio)}</div><div class="kpi-lbl">Desvio Orçamental</div></div>
      <div class="kpi-box"><div class="kpi-val">${pctExec}%</div><div class="kpi-lbl">Taxa de Execução</div></div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-box"><div class="kpi-val">${formatEuro(totalRealConc)}</div><div class="kpi-lbl">Executado (Concluídas)</div></div>
      <div class="kpi-box"><div class="kpi-val">${formatEuro(totalPrev - totalRealConc)}</div><div class="kpi-lbl">Por Executar</div></div>
      <div class="kpi-box"><div class="kpi-val">${concluidas.length}</div><div class="kpi-lbl">Ações Executadas</div></div>
      <div class="kpi-box"><div class="kpi-val">${formacoesAno.length - concluidas.length}</div><div class="kpi-lbl">Ações por Executar</div></div>
    </div>

    <div class="section-title">Detalhe por Ação de Formação</div>
    <table>
      <thead><tr>
        <th>Ação</th>
        <th>Financiamento</th>
        <th class="text-right">Prev. (€)</th>
        <th class="text-right">Real (€)</th>
        <th class="text-right">Desvio (€)</th>
        <th class="text-center">Exec. %</th>
        <th class="text-center">Estado</th>
      </tr></thead>
      <tbody>
        ${formacoesAno.map((f) => {
          const prev = Number(f.orcamento_previsto || 0);
          const real = Number(f.custo || 0);
          const dev = real - prev;
          const pct = prev > 0 ? Math.round((real / prev) * 100) : (real > 0 ? 100 : 0);
          return `<tr>
            <td class="font-bold" style="font-size:10px">${f.nome}</td>
            <td style="font-size:9px; color:#666">${f.financiamento ?? '—'}</td>
            <td class="text-right">${prev > 0 ? formatEuro(prev) : '—'}</td>
            <td class="text-right">${real > 0 ? formatEuro(real) : '—'}</td>
            <td class="text-right ${dev > 0 ? 'text-rose' : dev < 0 ? 'text-green' : ''} font-bold">${prev > 0 && real > 0 ? (dev > 0 ? '+' : '') + formatEuro(dev) : '—'}</td>
            <td class="text-center font-bold ${pct > 100 ? 'text-rose' : pct >= 80 ? 'text-green' : 'text-amber'}">${estadoNormalizado(f.estado) === 'concluida' ? pct + '%' : '—'}</td>
            <td class="text-center"><span class="badge ${estadoNormalizado(f.estado) === 'concluida' ? 'badge-green' : 'badge-zinc'}">${estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}</span></td>
          </tr>`;
        }).join('')}
        <tr style="background:#f8f8f8; font-weight:700;">
          <td colspan="2">TOTAL</td>
          <td class="text-right">${formatEuro(totalPrev)}</td>
          <td class="text-right">${formatEuro(totalReal)}</td>
          <td class="text-right ${desvio > 0 ? 'text-rose' : 'text-green'}">${desvio > 0 ? '+' : ''}${formatEuro(desvio)}</td>
          <td class="text-center">${pctExec}%</td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div class="section-title">Por Fonte de Financiamento</div>
    <table>
      <thead><tr>
        <th>Fonte de Financiamento</th>
        <th class="text-center">Ações</th>
        <th class="text-right">Orçamentado</th>
        <th class="text-right">Executado</th>
        <th class="text-right">Desvio</th>
      </tr></thead>
      <tbody>
        ${Object.entries(porFin).sort((a, b) => b[1].real - a[1].real).map(([fin, v]) => {
          const dev = v.real - v.prev;
          return `<tr>
            <td class="font-bold">${fin}</td>
            <td class="text-center">${v.count}</td>
            <td class="text-right">${v.prev > 0 ? formatEuro(v.prev) : '—'}</td>
            <td class="text-right">${v.real > 0 ? formatEuro(v.real) : '—'}</td>
            <td class="text-right ${dev > 0 ? 'text-rose' : dev < 0 ? 'text-green' : ''} font-bold">${v.prev > 0 && v.real > 0 ? (dev > 0 ? '+' : '') + formatEuro(dev) : '—'}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>

    <div class="footer">
      <div>Gestão da Formação Municipal — Relatório Orçamental Confidencial</div>
      <div>Gerado automaticamente em ${nowStr()}</div>
    </div>
    </div></body></html>`;

    abrirJanela(html, `relatorio-orcamental-formacao-${anoFiltro}.pdf`);
  }

  function abrirJanela(html: string, _filename: string) {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 800);
  }

  const TIPOS: { key: TipoRelatorio; label: string; icon: string; desc: string }[] = [
    {
      key: 'geral',
      label: 'Relatório Geral',
      icon: 'ri-bar-chart-grouped-line',
      desc: 'Resumo anual de todas as ações: participações, certificações, investimento e cobertura da formação.',
    },
    {
      key: 'funcionario',
      label: 'Por Funcionário',
      icon: 'ri-user-line',
      desc: 'Tabela completa dos 106 funcionários com horas, formações, certificados e última data de formação.',
    },
    {
      key: 'orcamental',
      label: 'Análise Orçamental',
      icon: 'ri-money-euro-circle-line',
      desc: 'Comparação entre orçamento previsto e custo real, desvios por ação e por fonte de financiamento.',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-file-pdf-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Exportar Relatório PDF</h2>
              <p className="text-[11px] text-zinc-400">Escolha o tipo e o ano a exportar</p>
            </div>
          </div>
          <button onClick={onClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-7 py-5 space-y-4">
          {/* Tipo de relatório */}
          <div>
            <p className="text-[12px] font-semibold text-zinc-600 mb-2">Tipo de Relatório</p>
            <div className="space-y-2">
              {TIPOS.map((t) => (
                <label
                  key={t.key}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    tipo === t.key ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo"
                    value={t.key}
                    checked={tipo === t.key}
                    onChange={() => setTipo(t.key)}
                    className="mt-0.5 cursor-pointer"
                  />
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                      <i className={`${t.icon} text-sm text-zinc-600`} />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-zinc-800">{t.label}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{t.desc}</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Ano */}
          <div>
            <p className="text-[12px] font-semibold text-zinc-600 mb-2">Ano</p>
            <div className="flex items-center gap-2">
              {anos.map((a) => (
                <button
                  key={a}
                  onClick={() => setAnoFiltro(a)}
                  className={`whitespace-nowrap flex-1 py-2.5 text-[13px] font-semibold rounded-lg border-2 transition-all cursor-pointer ${
                    anoFiltro === a ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="bg-zinc-50 rounded-xl p-3.5 flex items-start gap-2.5">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-sm text-zinc-400" />
            </div>
            <p className="text-[11px] text-zinc-500">
              O PDF abre numa nova aba para impressão ou guardar. Usa <strong>Ctrl+P</strong> (ou Cmd+P no Mac) e seleciona <strong>"Guardar como PDF"</strong> como destino de impressão.
            </p>
          </div>
        </div>

        <div className="px-7 pb-6 flex items-center gap-2.5 justify-end border-t border-zinc-100 pt-4">
          <button
            onClick={onClose}
            className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={gerarPdf}
            disabled={generating}
            className="whitespace-nowrap flex items-center gap-2 px-6 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                A gerar...
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-sm" /></div>
                Gerar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
